import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useOrg } from "../../context/OrgContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { WelcomeOnboardingModal } from "../../pages/dashboard/components/WelcomeOnboardingModal";

const STORAGE_PREFIX = "godo_welcome_dismissed_v1_";

function formatOrganizationName(orgId: string): string {
  const slug = orgId.replace(/^org_/i, "").replace(/[_-]+/g, " ").trim();
  if (!slug) return orgId;
  return slug.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function TrialWelcomeGate({ skipOnPos = false }: { skipOnPos?: boolean }) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { orgId, orgName } = useOrg();
  const { isTrial, isLoading } = useSubscription();
  const [open, setOpen] = useState(false);

  const storageKey = useMemo(
    () => `${STORAGE_PREFIX}${orgId ?? profile?.orgId ?? "global"}`,
    [orgId, profile?.orgId],
  );

  useEffect(() => {
    if (skipOnPos) return;
    if (isLoading || !profile) return;
    if (!isTrial) return;
    if (localStorage.getItem(storageKey) === "1") return;
    const timer = window.setTimeout(() => setOpen(true), 400);
    return () => window.clearTimeout(timer);
  }, [isLoading, isTrial, profile, skipOnPos, storageKey]);

  const dismissPermanent = useCallback(() => {
    localStorage.setItem(storageKey, "1");
    setOpen(false);
  }, [storageKey]);

  if (skipOnPos || !profile) return null;

  const organizationName =
    orgName?.trim() ||
    profile.companyName?.trim() ||
    formatOrganizationName(profile.orgId);
  const displayName = profile.displayName || profile.login;

  return (
    <WelcomeOnboardingModal
      isOpen={open}
      organizationName={organizationName}
      displayName={displayName}
      onClose={() => setOpen(false)}
      onDontShowAgain={dismissPermanent}
      onGoSettings={() => {
        setOpen(false);
        navigate("/settings");
      }}
      onGoInventory={() => {
        setOpen(false);
        navigate("/inventory");
      }}
      onGoSales={() => {
        setOpen(false);
        navigate("/pos");
      }}
    />
  );
}
